import { Box, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { Alert, AlertTitle } from '@material-ui/lab';
import { FirmUnderwriterItem } from 'api/admin/types';
import clsx from 'clsx';
import { AsyncStatus } from 'features/files/fincen-reporting/constants';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { FC, useEffect, useMemo, useState } from 'react';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { AgentNetCheckBox } from 'ui-kit/inputs';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import { UNDERWRITER_PRODUCT_TEST_IDS } from './feature-details-tabs';
import { getUnderwriterIcon } from './icons';
import {
  buildInitialUnderwriterSelection,
  getFilteredUnderwriters,
  getSelectableUnderwriters,
  getSelectAllState,
  isUnderwriterChecked,
  isUnderwriterLocked,
  toggleSelectAll,
  toggleUnderwriterSelection,
} from './underwriterSelectionUtils';
import { EmptyState, pxToRem } from '@fluentsms/agentnet-web-components';
import { SearchResultsEmptyState } from '@fluentsms/agentnet-web-icons';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerBody: {
      padding: pxToRem(75, 24, 75, 24),
    },
    drawerTitle: {
      color: theme.palette.text.primary,
    },
    drawerSubTitle: {
      display: 'block',
      fontSize: pxToRem(14),
      lineHeight: pxToRem(20),
      marginTop: theme.spacing(0.5),
      fontWeight: 500,
      '& strong': {
        fontWeight: 600,
      },
    },
    searchField: {
      marginBottom: theme.spacing(2),
      border: '0 !important',
    },
    // Fills the space left between the search field and the fee banner so the
    // empty state sits centered rather than hugging the search field.
    emptyStateContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 320px)',
      width: '100%',
    },
    selectAllRow: {
      alignItems: 'center',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      marginBottom: theme.spacing(2),
      minHeight: 48,
      padding: theme.spacing(1, 2),
      width: '100%',
      '& .MuiFormControlLabel-root': {
        margin: 0,
      },
    },
    underwriterCard: {
      alignItems: 'center',
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      gap: theme.spacing(1.5),
      minHeight: 72,
      padding: theme.spacing(1.5, 2),
      transition: theme.transitions.create(['background-color', 'border-color']),
    },
    underwriterCardSelected: {
      backgroundColor: theme.palette.action.selected,
      borderColor: theme.palette.primary.main,
    },
    underwriterIcon: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      height: 48,
      justifyContent: 'center',
      width: 48,
      '& img': {
        display: 'block',
        height: '100%',
        objectFit: 'contain',
        width: '100%',
      },
    },
    underwriterName: {
      flex: 1,
    },
    alertContainer: {
      marginBottom: '62px',
    },
    warningBanner: {
      borderRadius: 0,
      borderTop: '1px solid #c4c4c4',
      bottom: '49px',
      left: 0,
      maxWidth: 'unset !important',
      padding: '6px 24px',
      position: 'absolute',
      width: '-webkit-fill-available !important',
      zIndex: 3,
    },
  }),
);

interface ManageUnderwritersDrawerProps {
  open: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  onSaveError: () => void;
  underwriters: FirmUnderwriterItem[];
  loading?: boolean;
}

export const ManageUnderwritersDrawer: FC<ManageUnderwritersDrawerProps> = ({
  open,
  onClose,
  onSaveSuccess,
  onSaveError,
  underwriters,
  loading = false,
}) => {
  const classes = useStyles();
  const { saveFirmUnderwriters, saveFirmUnderwritersStatus } = useMarketPlace();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => buildInitialUnderwriterSelection(underwriters));

  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedIds(buildInitialUnderwriterSelection(underwriters));
    }
  }, [open, underwriters]);

  const filteredUnderwriters = useMemo(
    () => getFilteredUnderwriters(underwriters, searchQuery),
    [searchQuery, underwriters],
  );

  const selectableFilteredUnderwriters = useMemo(
    () => getSelectableUnderwriters(filteredUnderwriters),
    [filteredUnderwriters],
  );

  const { allSelected, someSelected } = useMemo(
    () => getSelectAllState(selectableFilteredUnderwriters, selectedIds),
    [selectableFilteredUnderwriters, selectedIds],
  );

  const isSaving = saveFirmUnderwritersStatus === AsyncStatus.PENDING;

  const handleSave = () => {
    const selectedNonFaUnderwriterCodes = underwriters
      .filter((underwriter) => !isUnderwriterLocked(underwriter) && selectedIds.has(underwriter.Id))
      .map((underwriter) => underwriter.Code);

    saveFirmUnderwriters(selectedNonFaUnderwriterCodes);
  };

  // Global error messaging for a failed save is handled by useMarketPlace saveFirmUnderwritersErrors.
  useEffect(() => {
    if (saveFirmUnderwritersStatus === AsyncStatus.SUCCESS) {
      onSaveSuccess();
    }

    if (saveFirmUnderwritersStatus === AsyncStatus.ERROR) {
      onSaveError();
    }
  }, [saveFirmUnderwritersStatus]);

  return (
    <FormDrawerComponent
      open={open}
      onDismissAction={onClose}
      onPrimaryAction={handleSave}
      primaryActionLabel="Save Changes"
      crossQAPrefix={UNDERWRITER_PRODUCT_TEST_IDS.manageUnderwritersDrawer.crossQAPrefix}
      id="manage-underwriters-drawer"
      bodyClassName={classes.drawerBody}
      title="Manage Underwriters"
      titleClassName={classes.drawerTitle}
      subTitle={
        <span className={classes.drawerSubTitle}>
          Selections will be available for the <strong>Contact Validation</strong>,{' '}
          <strong>Identity Verification</strong>, and <strong>Wire Validation</strong> add-ons.
        </span>
      }
      width={900}
      disabled={loading || isSaving}
      disableCrossIconClick={isSaving}
      dismissActionProps={{
        disabled: isSaving,
        'data-testid': UNDERWRITER_PRODUCT_TEST_IDS.manageUnderwritersDrawer.cancel,
      }}
      primaryActionProps={{
        variant: 'contained',
        loading: isSaving,
        'data-testid': UNDERWRITER_PRODUCT_TEST_IDS.manageUnderwritersDrawer.save,
      }}
    >
      {loading ? (
        <LoadingSpinner variant="circle" status={AsyncStatus.PENDING} />
      ) : (
        <>
          <AgentNetTextInput
            fullWidth
            label=""
            placeholder="Search Underwriters"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className={classes.searchField}
            startAdornment={<SearchIcon color="disabled" fontSize="small" />}
            data-testid={UNDERWRITER_PRODUCT_TEST_IDS.manageUnderwritersDrawer.search}
          />

          {filteredUnderwriters.length === 0 ? (
            <Box
              className={classes.emptyStateContainer}
              data-testid={UNDERWRITER_PRODUCT_TEST_IDS.manageUnderwritersDrawer.noResults}
            >
              <EmptyState
                headerText="No Results"
                descriptionText="Try adjusting your search to find what you're looking for."
                icon={<SearchResultsEmptyState />}
              />
            </Box>
          ) : (
            <>
              <Box className={classes.selectAllRow}>
                <AgentNetCheckBox
                  label="Select All"
                  name="manage-underwriters-select-all"
                  value={allSelected}
                  indeterminate={someSelected && !allSelected}
                  disabled={selectableFilteredUnderwriters.length === 0}
                  checkHandler={() => {
                    setSelectedIds((currentSelectedIds) =>
                      toggleSelectAll(selectableFilteredUnderwriters, currentSelectedIds),
                    );
                  }}
                  data-testid={UNDERWRITER_PRODUCT_TEST_IDS.manageUnderwritersDrawer.selectAll}
                />
              </Box>

              <Grid container spacing={2}>
                {filteredUnderwriters.map((underwriter, rowIndex) => {
                  const iconSrc = getUnderwriterIcon(underwriter.Name);
                  const locked = isUnderwriterLocked(underwriter);
                  const checked = isUnderwriterChecked(underwriter, selectedIds);

                  return (
                    <Grid item xs={12} sm={6} key={underwriter.Id}>
                      <Box
                        className={clsx(classes.underwriterCard, {
                          [classes.underwriterCardSelected]: checked && !locked,
                        })}
                        data-testid={`${UNDERWRITER_PRODUCT_TEST_IDS.manageUnderwritersDrawer.underwriterCard}-${underwriter.Code}`}
                      >
                        <AgentNetCheckBox
                          label=""
                          name={`manage-underwriter-${underwriter.Id}`}
                          value={checked}
                          disabled={locked}
                          checkHandler={() => {
                            setSelectedIds((currentSelectedIds) =>
                              toggleUnderwriterSelection(underwriter, currentSelectedIds),
                            );
                          }}
                          data-testid={`${UNDERWRITER_PRODUCT_TEST_IDS.manageUnderwritersDrawer.underwriterCheckbox}-${rowIndex}`}
                        />
                        <Box className={classes.underwriterIcon}>
                          <img src={iconSrc} alt={underwriter.Name} />
                        </Box>
                        <Typography variant="body1" className={classes.underwriterName}>
                          {underwriter.Name}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          <div className={classes.alertContainer} />

          <Alert
            severity="warning"
            className={classes.warningBanner}
            data-testid={UNDERWRITER_PRODUCT_TEST_IDS.manageUnderwritersDrawer.warning}
          >
            <AlertTitle>Fees Applicable to Non-First American Transactions</AlertTitle>
            Selecting non-First American Underwriters enables paid validations for your firm and{' '}
            <strong>each validation request will incur a fee</strong>. Pricing details available in the About tab.
          </Alert>
        </>
      )}
    </FormDrawerComponent>
  );
};
